import usePortalQuery from 'src/Libs/api/hooks/use-portal-query'
import { ChartPriceItem, NullablePowerPrice, PowerPrice } from 'src/Libs/dynamic-power-prices/types'
import moment from '@e3dc-react/shell/Libs/moment'
import { fetchPowerPrices } from 'src/Libs/dynamic-power-prices/api/power-price.api'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { convertPriceData, removeOutOfRangePrices } from '../utils'
import { useMemo } from 'react'

interface Props {
  installationId: string | undefined
  /* The time range for which to fetch electricity prices */
  from: Date
  to: Date
}

/**
 * hook for fetching electricity prices for AI360.
 * it fetches data for a specific installation and time range.
 */
export const useAI360PowerPrice = ({ installationId, from, to }: Props): ChartPriceItem[] => {
  const authFetch = useAuthFetchWithResponse()

  /* Convert moment-dates to ISO strings for reliable query keys */
  const fromISO = from.toISOString()
  const toISO = to.toISOString()

  const fromMoment = useMemo(() => moment(from), [from])
  const toMoment = useMemo(() => moment(to), [to])

  const { data: powerPrices } = usePortalQuery<NullablePowerPrice[], PowerPrice[]>({
    queryKey: ['ai360-power-prices', installationId, fromISO, toISO],
    queryFn: () => fetchPowerPrices(installationId, authFetch, fromMoment, toMoment, 'hour'),
    enabled: !!installationId,
    select: (response: NullablePowerPrice[] | null): PowerPrice[] => {
      if (!response) return []

      const pricesWithoutNullValues: PowerPrice[] = response.map(value => ({
        price: value?.price ?? 0,
        validFrom: value?.validFrom,
        validTo: value?.validTo,
      }))

      return pricesWithoutNullValues
    },
  })

  const powerPriceDataInTimeRange = removeOutOfRangePrices(powerPrices ?? [], from, to)
  const powerPriceData = convertPriceData({ priceData: powerPriceDataInTimeRange, from, to })

  return powerPriceData
}
